/**
 * Google Analytics 4 (GA4) Event Tracking Utility
 * 
 * CRITICAL: This module is designed to NEVER block or interrupt payment flows.
 * All functions fail silently and return immediately if gtag is unavailable.
 * 
 * @see https://developers.google.com/analytics/devguides/collection/ga4/events
 */

// Measurement ID from environment or fallback to production
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || 'G-Q70MZP79Y7';

/**
 * Check if gtag is available in the window object
 * Prevents errors when analytics scripts haven't loaded yet
 * NEVER throws errors - always returns boolean
 */
const isGtagAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  } catch {
    return false; // Fail silently
  }
};

/**
 * Enhanced Purchase/Subscription Event Parameters
 * Following GA4 enhanced ecommerce
 * 
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */
export interface SubscriptionEventParams {
  transaction_id: string;        // Unique transaction/order ID
  value: number;                 // Total revenue 
  currency: string;              // ISO 4217 currency code (e.g., 'INR', 'USD')
  subscription_plan: string;     // Plan name/ID (e.g., '14-day', '30-day', '3-month')
  payment_method: string;        // Payment gateway used (e.g., 'razorpay', 'dodo')
  plan_duration_days?: number;   // Subscription duration in days
  items?: Array<{                // Optional: detailed item information
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
  // Optional affiliate tracking parameters
  affiliate_id?: string;
  affiliate_campaign?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

/**
 * Track successful subscription purchase
 * Fires a 'purchase' event which is automatically counted as a conversion in GA4
 * 
 * @param params - Subscription event parameters
 * 
 * @example
 * trackSubscriptionPurchase({
 *   transaction_id: 'order_abc123',
 *   value: 99.00,
 *   currency: 'INR',
 *   subscription_plan: '30-day-premium',
 *   payment_method: 'razorpay',
 *   plan_duration_days: 30
 * });
 */
export const trackSubscriptionPurchase = (params: SubscriptionEventParams): void => {
  // Early return if gtag not available - NEVER block payment flow
  try {
    if (!isGtagAvailable()) {
      return; // Silently skip tracking
    }

    // Double-wrapped in try-catch for maximum safety
    try {
      window.gtag('event', 'subscription_purchased', {
        transaction_id: params.transaction_id,
        value: params.value,
        currency: params.currency,
        subscription_plan: params.subscription_plan,
        payment_method: params.payment_method,
        plan_duration_days: params.plan_duration_days,
        items: params.items || [{
          item_id: params.subscription_plan,
          item_name: `ResumeMate ${params.subscription_plan}`,
          price: params.value,
          quantity: 1
        }],
        affiliate_id: params.affiliate_id,
        affiliate_campaign: params.affiliate_campaign,
        utm_source: params.utm_source,
        utm_medium: params.utm_medium,
        utm_campaign: params.utm_campaign,
      });
    } catch (innerError) {
      // Silently fail - never interrupt payment
    }
  } catch (outerError) {
    // Ultimate safety net - catch everything
  }
};

/**
 * Track subscription initiation (when user clicks subscribe button)
 * SAFETY: Never blocks payment flow
 */
export const trackSubscriptionInitiated = (planId: string, value: number, currency: string): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'subscription_initiated', {
        currency: currency,
        value: value,
        items: [{
          item_id: planId,
          item_name: `ResumeMate Plan ${planId}`,
          price: value,
          quantity: 1
        }]
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track payment method selection
 * SAFETY: Never blocks payment flow
 */
export const trackPaymentMethodSelected = (method: 'razorpay' | 'dodo', planId: string): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'add_payment_info', {
        payment_type: method,
        subscription_plan: planId
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track subscription failures
 * SAFETY: Never blocks payment flow
 */
export const trackSubscriptionFailure = (
  reason: string,
  planId: string,
  paymentMethod: string
): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'subscription_failed', {
        event_category: 'subscription',
        event_label: reason,
        subscription_plan: planId,
        payment_method: paymentMethod
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track resume download from builder page
 * SAFETY: Never blocks user flow
 */
export const trackDownloadBuilder = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'download_builder', {
        event_category: 'resume',
        event_label: 'builder_page_download'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track resume download from score check page
 * SAFETY: Never blocks user flow
 */
export const trackDownloadScore = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'download_score', {
        event_category: 'resume',
        event_label: 'score_page_download'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track check score button click from builder page
 * SAFETY: Never blocks user flow
 */
export const trackCheckScoreBuilder = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'checkScore_builder', {
        event_category: 'score',
        event_label: 'builder_page_check_score'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track experience selection on select-experience page
 * SAFETY: Never blocks user flow
 */
export const trackCheckScoreSelectExperience = (experienceLevel: string): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'checkScore_selectExperience', {
        event_category: 'score',
        event_label: 'select_experience_page',
        experience_level: experienceLevel
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track premium upgrade click from builder navbar diamond button
 * SAFETY: Never blocks user flow
 */
export const trackPremiumUpgradeNavBuilder = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'premium_upgrade_nav_builder', {
        event_category: 'premium',
        event_label: 'navbar_diamond_button_builder'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track premium upgrade click from score-check navbar diamond button
 * SAFETY: Never blocks user flow
 */
export const trackPremiumUpgradeNavScore = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'premium_upgrade_nav_score', {
        event_category: 'premium',
        event_label: 'navbar_diamond_button_score'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track premium upgrade click from left sidebar premium card on builder page
 * SAFETY: Never blocks user flow
 */
export const trackPremiumUpgradeLeftsideBuilder = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'premium_upgrade_leftside_builder', {
        event_category: 'premium',
        event_label: 'leftside_card_builder'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track premium upgrade click from left sidebar premium card on score-check page
 * SAFETY: Never blocks user flow
 */
export const trackPremiumUpgradeLeftsideScore = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'premium_upgrade_leftside_score', {
        event_category: 'premium',
        event_label: 'leftside_card_score'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track premium upgrade click from download explore plans overlay
 * SAFETY: Never blocks user flow
 */
export const trackPremiumUpgradeDownload = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'premium_upgrade_download', {
        event_category: 'premium',
        event_label: 'explore_plans_overlay'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track referral click from home page navbar
 * SAFETY: Never blocks user flow
 */
export const trackReferralNavHome = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'referral_nav_home', {
        event_category: 'referral',
        event_label: 'navbar_home_page'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track referral click from footer
 * SAFETY: Never blocks user flow
 */
export const trackReferralFooter = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'referral_footer', {
        event_category: 'referral',
        event_label: 'footer_services'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track referral click from left sidebar premium card on builder page
 * SAFETY: Never blocks user flow
 */
export const trackReferralLeftsideBuilder = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'referral_leftside_builder', {
        event_category: 'referral',
        event_label: 'leftside_card_builder'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track referral click from left sidebar premium card on score-check page
 * SAFETY: Never blocks user flow
 */
export const trackReferralLeftsideScore = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'referral_leftside_score', {
        event_category: 'referral',
        event_label: 'leftside_card_score'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track referral click from builder navbar breadcrumb
 * SAFETY: Never blocks user flow
 */
export const trackReferralBreadcrumbBuilder = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'referral_breadcrumb_builder', {
        event_category: 'referral',
        event_label: 'breadcrumb_builder'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Track referral click from score-check navbar breadcrumb
 * SAFETY: Never blocks user flow
 */
export const trackReferralBreadcrumbScore = (): void => {
  try {
    if (!isGtagAvailable()) return;
    try {
      window.gtag('event', 'referral_breadcrumb_score', {
        event_category: 'referral',
        event_label: 'breadcrumb_score'
      });
    } catch { /* Fail silently */ }
  } catch { /* Fail silently */ }
};

/**
 * Get UTM parameters from URL or session storage
 * SAFETY: Returns null on any error, never throws
 */
export const getUTMParameters = (): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
} | null => {
  try {
    if (typeof window === 'undefined') return null;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const params: any = {};
      
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
        try {
          const value = urlParams.get(param);
          if (value) params[param] = value;
        } catch { /* Skip this parameter */ }
      });

      if (Object.keys(params).length > 0) {
        try {
          sessionStorage.setItem('utm_params', JSON.stringify(params));
        } catch { /* Session storage unavailable */ }
        return params;
      }

      try {
        const stored = sessionStorage.getItem('utm_params');
        if (stored) return JSON.parse(stored);
      } catch { /* Session storage unavailable */ }

      return null;
    } catch {
      return null; // Any parsing error
    }
  } catch {
    return null; // Ultimate safety net
  }
};

/**
 * Type declarations for window.gtag
 */
declare global {
  interface Window {
    gtag: (
      command: 'event' | 'config' | 'js' | 'set',
      targetOrAction: string | Date,
      params?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}
